import React from 'react';
import { cn } from '../../utils';
import { useTranslation } from 'react-i18next';
import FieldLoading from './FieldLoading';
import Row from './Containers/Row';
import { Search } from 'lucide-react';
import { useLocale } from '../../hooks/useLocale';

export interface InputFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  placeholder?: string;
  error?: string;
  label?: string;
  containerClassName?: string;
  isRequired?: boolean;
  isOptional?: boolean;
  onValueChange?: (value: string) => void;
  isLoading?: boolean;
  labelClassName?: string;
  isDigitsOnly?: boolean;
  //
  isFullWidth?: boolean;
}

const TextInputField: React.FC<InputFieldProps> = ({
  placeholder,
  error,
  label,
  isRequired,
  isOptional,
  containerClassName = '',
  className = '',
  onValueChange,
  isLoading,
  labelClassName,
  isDigitsOnly,
  isFullWidth = false,
  readOnly,
  ...props
}) => {
  return (
    <TextInputContainer
      label={label}
      labelClassName={labelClassName}
      error={error}
      isRequired={isRequired}
      isOptional={isOptional}
      isFullWidth={isFullWidth}
      containerClassName={containerClassName}
    >
      <FieldLoading isLoading={isLoading}>
        <TextInput
          isRequired={isRequired}
          placeholder={placeholder}
          className={className}
          onValueChange={onValueChange}
          isDigitsOnly={isDigitsOnly}
          readOnly={readOnly}
          error={error}
          {...props}
        />
      </FieldLoading>
    </TextInputContainer>
  );
};

export default TextInputField;

const TextInput: React.FC<InputFieldProps> = ({
  isRequired,
  placeholder,
  className,
  onValueChange,
  isDigitsOnly,
  readOnly,
  error,
  ...props
}) => {
  return (
    <input
      required={isRequired}
      className={cn(
        'border-[1.5px] animated border-borderColor rounded-xl h-14 outline-none focus:border-primary p-3 placeholder:text-hintText placeholder:text-sm transition-colors disabled:bg-borderColor/30 disabled:text-lightGrayText',
        error && 'border-red-500 focus:border-red-500',
        readOnly && 'text-lightGrayText bg-borderColor/15 cursor-not-allowed',
        className
      )}
      readOnly={readOnly}
      placeholder={placeholder}
      {...props}
      onChange={(e) => {
        if (isDigitsOnly) {
          const value = e.target.value;
          if (/^\d*$/.test(value)) {
            onValueChange?.(value);
          }
        } else {
          onValueChange?.(e.target.value);
        }
      }}
    />
  );
};

const TextInputLabelText = ({
  label,
  error,
  isRequired,
  isOptional,
  className,
}: {
  label?: string;
  error?: string;
  isRequired?: boolean;
  isOptional?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();

  if (!label) return <></>;
  return (
    <label
      className={cn(
        'text-sm font-medium text-labelText px-1',
        error && 'text-red-500',
        className
      )}
    >
      {isRequired && <span className={cn('pr-1', error && 'text-red-500')}>*</span>}
      {label}
      {isOptional && (
        <span className="text-sm font-normal text-hintText">
          {` (${t('common.optional')})`}
        </span>
      )}
    </label>
  );
};

const TextInputContainer: React.FC<InputFieldProps> = ({
  label,
  children,
  error,
  isRequired,
  isOptional,
  containerClassName,
  labelClassName,
  isFullWidth,
}) => {
  return (
    <div
      className={cn(
        'flex flex-col gap-2',
        isFullWidth && 'w-full',
        containerClassName
      )}
    >
      <TextInputLabelText
        label={label}
        error={error}
        isRequired={isRequired}
        isOptional={isOptional}
        className={labelClassName}
      />
      {children}
      {error && <span className="text-red-500 text-xs mt-1">{error}</span>}
    </div>
  );
};
export { TextInputContainer, TextInputLabelText };

export const SearchInputField: React.FC<InputFieldProps> = ({
  isLoading,
  placeholder,
  containerClassName,
  className,
  ...props
}) => {
  const { t } = useTranslation();
  const { isArabic } = useLocale();
  return (
    <TextInputContainer {...props}>
      <FieldLoading isLoading={isLoading}>
        <Row className="p-0 relative">
          <TextInput
            className={cn('h-12', isArabic ? 'pr-10' : 'pl-10', className)}
            placeholder={placeholder ?? t('components.search_bar.search')}
            {...props}
          />
          <Search
            className={cn(
              'w-4 h-4 text-hintText absolute top-4',
              isArabic ? 'right-3' : 'left-3'
            )}
          />
        </Row>
      </FieldLoading>
    </TextInputContainer>
  );
};

export const TextIconInputField = ({
  isLoading,
  placeholder,
  containerClassName,
  className,
  startIcon,
  endIcon,
  ...props
}: InputFieldProps & {
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const { isArabic } = useLocale();
  return (
    <TextInputContainer {...props}>
      <FieldLoading isLoading={isLoading}>
        <Row className="p-0 relative">
          <TextInput
            className={cn(
              'min-w-full',
              // Start Icon
              startIcon && isArabic ? 'pr-11' : 'pl-11',
              // End Icon
              !!endIcon && (isArabic ? 'pl-11' : 'pr-11'),
              className
            )}
            placeholder={placeholder ?? t('components.search_bar.search')}
            {...props}
          />

          {startIcon && (
            <div
              className={cn(
                'absolute top-5 w-fit h-fit',
                isArabic ? 'right-4' : 'left-4'
              )}
            >
              {startIcon}
            </div>
          )}
          {endIcon && (
            <div
              className={cn(
                'absolute top-5 w-fit h-fit',
                isArabic ? 'left-4' : 'right-4'
              )}
            >
              {endIcon}
            </div>
          )}
        </Row>
      </FieldLoading>
    </TextInputContainer>
  );
};
